import CD from 'cropduster';
import StudioApp from 'studio-app';
import { wrapLink } from './utils';
import environment from './environment';
import { landingPage } from './environment';
import eventManager from './event';

export default class ArApp extends StudioApp {
  constructor() {
    super();

    this.EM = new eventManager(CD, environment);

    this.container = document.querySelector('#mi_size_container');
    this.panelContainer = this.wrapContainer(this.container);

    this.removeLabels();
  }

  get appKey() {
    return CD.params('mi_app_key') || CD.params('app_key');
  }

  isCapturama() {
    return typeof MICapture !== 'undefined';
  }

  makeVideoPanel() {
    const element = document.createElement('div');
    const { top, left, width, height } = this.container.getBoundingClientRect();
    element.style.position = 'absolute';
    element.style.top = `${top}px`;
    element.style.left = `${left}px`;
    element.style.width = `${width}px`;
    element.style.height = `${height}px`;
    element.classList.add('video-panel');

    const videoTags = this.tags.filter(t => !t.classNames.match(/(-panel|hidden-label)/));
    videoTags.forEach(tag => {
      element.appendChild(tag.element);
    });

    this.panelContainer.prepend(element);

    return {
      element,
      top,
      left,
      width,
      height,
      subtags: videoTags
    };
  }

  wrapContainer(container) {
    // panelContainer is inside container, and contains all of the panels.
    // As we transition panels, panelContainer changes its transform offset.
    const panelContainer = document.createElement('div');
    panelContainer.classList.add('container');

    container.prepend(panelContainer);
    container.style.display = 'block';

    return panelContainer;
  }

  removeLabels() {
    this.tags
      .filter(t => t.classNames === 'hidden-label')
      .forEach(t => {
        t.element.remove();
      });

    this._tags = this.tags.filter(t => t.classNames !== 'hidden-label');
    this._allTags = this.allTags.filter(t => t.classNames !== 'hidden-label');
  }

  transitionToPanel(name, instant = false) {
    const panel = this.panels.find(p => p.panelName === name);
    if (!panel) {
      throw `Missing panel: ${name}`;
    }
    if (panel === this.currentPanel) {
      return;
    }

    if (!panel.isRendered) {
      panel.render();
      panel.isRendered = true;
    }

    const oldPanel = this.currentPanel;
    panel.activate();
    this.currentPanel = panel;

    if (instant) {
      this.panelContainer.style.transitionDuration = '0s';
    }

    this.panelContainer.style.transform = `translate(-${panel.offset}px, 0)`;

    if (instant) {
      delete this.panelContainer.style.transitionDuration;
    }
    if (oldPanel) oldPanel.deactivate();
  }

  // Find all panel groups and sort them spatially by panelOrder on the storyboard
  collectPanels(panelClasses) {
    let offset = 0;
    let index = 0;

    return panelClasses
      .map(panelType => {
        let panel;

        if (panelType.name === 'VideoPanel') {
          panel = this.makeVideoPanel();
        } else {
          panel = this.tags.find(t => t.classNames.indexOf(`${panelType.className}`) >= 0);
          if (!panel) return null;
        }

        this.panelContainer.appendChild(panel.element);

        panel.top = 0;
        panel.left = panel.offset = offset;
        panel.element.style.top = '0';
        panel.element.style.left = `${panel.offset}px`;
        panel.element.style.overflow = 'hidden';
        offset += panel.width;

        return new panelType(panel, this, index++);
      })
      .filter(Boolean);
  }

  redirectToAR() {
    CD.setImageRedirect(landingPage());
    window.APP_SUCCESSFULLY_RENDERED = true;
  }

  initializeVideo() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error('Missing WebRTC support');
    }

    let video;

    if (this.options.camera_direction === 'environment') {
      video = { facingMode: { exact: 'environment' } };
      this.mirrorVideo = false;
    } else {
      video = true;
      this.mirrorVideo = true;
    }

    const constraints = (window.constraints = {
      audio: false,
      video
    });

    return navigator.mediaDevices.getUserMedia(constraints).catch(error => {
      if (error.name === 'OverconstrainedError' && this.mirrorVideo === false) {
        // tried to get environment video, fall back to facing video
        constraints.video = true;
        this.mirrorVideo = true;
        return navigator.mediaDevices.getUserMedia(constraints);
      } else {
        throw error;
      }
    });
  }

  handleCameraError(err) {
    console.error(err);
    this.EM.send('no_camera');
    this.transitionToPanel('error');
  }

  render() {
    this.transitionToPanel('loading');

    // do not allow gestures or dragging
    document.addEventListener('gesturestart', e => e.preventDefault());
    document.addEventListener('touchmove', e => e.preventDefault());

    window.addEventListener('orientationchange', () => this.doOnOrientationChange());
    // Initial execution if needed
    this.doOnOrientationChange();
  }

  doOnOrientationChange() {
    if (window.orientation == 90 || window.orientation == -90) {
      this.lastPanel = this.currentPanel && this.currentPanel.panelName;
      this.transitionToPanel('landscape', true);
    } else {
      if (this.currentPanel && this.currentPanel.panelName === 'landscape') {
        this.transitionToPanel(this.lastPanel || 'video', true);
      }
    }
  }

  setupClickthroughs() {
    this.allTags
      .filter(t => t.toolName === 'clickthrough')
      .forEach(tag => {
        const { element, toolProperties } = tag;
        if (element && toolProperties && toolProperties.url) {
          wrapLink(element, {
            url: toolProperties.url,
            className: 'clickthrough-region',
            target: '_blank'
          });
          // set up event listener to share clickthroughs
          if (tag.classNames !== 'ar-clickthrough') {
            element.addEventListener('click', () => {
              this.EM.send('share', {
                share_to: 'register_click',
                share_url: toolProperties.url
              });
            });
          }
        }
      });
  }

  setupEngagementEvents() {
    this.EM.setupEngagementEvents();
  }

  sendEvent(...args) {
    return this.EM.send(...args);
  }
}
